if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.variantChangeUnsubscriber = null;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        this.variantInput = this.querySelector('input[name="id"]');
        this.quantityInput = this.querySelector('input[name="quantity"]');
        this.submitButton = this.querySelector(`#StickySubmitButton-${this.sectionId}`);
        this.submitLabel = this.submitButton?.querySelector('.sticky-add-to-cart__submit-label');
        this.activePriceEl = this.querySelector('[data-sticky-active-price]');
        this.comparePriceEl = this.querySelector('[data-sticky-compare-price]');
        this.priceContainer = this.querySelector(`#StickyPrice-${this.sectionId}`);

        this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option-select]'));
        this.frequencyWrapper = this.querySelector('[data-sticky-frequency-wrapper]');
        this.frequencySelect = this.querySelector('[data-sticky-frequency]');
        this.sellingPlanInput = this.querySelector('[data-sticky-selling-plan-input]');
        this.purchaseTypeInputs = Array.from(this.querySelectorAll('[data-sticky-purchase-type]'));

        this.priceData = this.parsePriceData();

        this.bindEvents();
        this.setupIntersectionObserver();
        this.subscribeToVariantChange();

        const checkedPurchaseType = this.querySelector('[data-sticky-purchase-type]:checked');
        if (checkedPurchaseType) {
          this.updateSellingPlanState(checkedPurchaseType.value);
        } else {
          this.updatePriceDisplay();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      parsePriceData() {
        const node = this.querySelector('[data-sticky-price-data]');
        if (!node) return { variants: [] };
        try {
          return JSON.parse(node.textContent);
        } catch (err) {
          console.warn('TEST:: sticky-add-to-cart: failed to parse price data', err);
          return { variants: [] };
        }
      }

      bindEvents() {
        this.optionSelects.forEach((select) => {
          select.addEventListener('change', (event) => this.handleOptionChange(event));
        });

        this.addEventListener('change', (event) => {
          const target = event.target;
          if (!target) return;
          if (target.matches('[data-sticky-purchase-type]')) {
            this.updateSellingPlanState(target.value);
          } else if (target.matches('[data-sticky-frequency]')) {
            if (this.sellingPlanInput && !this.sellingPlanInput.disabled) {
              this.sellingPlanInput.value = target.value;
            }
            this.updatePriceDisplay();
          }
        });
      }

      setupIntersectionObserver() {
        const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        if (!productInfo) {
          console.warn(`TEST:: sticky-add-to-cart: product-info not found for section ${this.sectionId}`);
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setVisible(scrolledPast);
          },
          { threshold: 0 }
        );
        this.observer.observe(productInfo);
      }

      setVisible(visible) {
        if (visible) {
          this.hidden = false;
          requestAnimationFrame(() => {
            this.classList.add('sticky-add-to-cart--visible');
          });
        } else {
          this.classList.remove('sticky-add-to-cart--visible');
          this.addEventListener(
            'transitionend',
            (event) => {
              if (event.target !== this || event.propertyName !== 'transform') return;
              if (!this.classList.contains('sticky-add-to-cart--visible')) {
                this.hidden = true;
              }
            },
            { once: true }
          );
        }
      }

      subscribeToVariantChange() {
        if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data || event.data.sectionId !== this.sectionId) return;
          this.syncFromVariantChange(event.data);
        });
      }

      syncFromVariantChange({ html, variant }) {
        this.updateVariantInput(variant);
        this.updateSubmitFromHtml(html);
        this.syncOptionSelectsToVariant(variant);
        this.updatePriceDisplay();
      }

      updateVariantInput(variant) {
        if (!this.variantInput) return;
        if (variant) {
          this.variantInput.value = variant.id;
          this.variantInput.toggleAttribute('disabled', !variant.available);
        } else {
          this.variantInput.value = '';
          this.variantInput.toggleAttribute('disabled', true);
        }
      }

      updateSubmitFromHtml(html) {
        if (!html || !this.submitButton) return;
        const mainButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!mainButton) return;
        const disabled = mainButton.hasAttribute('disabled');
        this.submitButton.toggleAttribute('disabled', disabled);
        const srcLabel = mainButton.querySelector('span');
        if (srcLabel && this.submitLabel) {
          this.submitLabel.textContent = srcLabel.textContent.trim();
        }
      }

      syncOptionSelectsToVariant(variant) {
        if (!variant || this.optionSelects.length === 0) return;
        const values = [variant.option1, variant.option2, variant.option3];
        this.optionSelects.forEach((select) => {
          const position = parseInt(select.dataset.optionPosition, 10);
          if (!Number.isFinite(position)) return;
          const value = values[position - 1];
          if (value != null && select.value !== value) {
            select.value = value;
          }
        });
      }

      handleOptionChange(event) {
        const select = event.target;
        const optionName = select.dataset.optionName;
        const optionValue = select.value;
        if (!optionName) return;

        const forwarded = this.forwardOptionToMainPicker(optionName, optionValue);
        if (!forwarded) {
          console.warn(
            `TEST:: sticky-add-to-cart: could not forward option ${optionName}=${optionValue} to main picker`
          );
        }
      }

      forwardOptionToMainPicker(optionName, optionValue) {
        const mainPicker = document.querySelector(
          `#MainProduct-${this.sectionId} variant-selects, variant-selects[data-section="${this.sectionId}"]`
        );
        if (!mainPicker) return false;

        const nameAttr = `options[${optionName}]`;

        const radio = mainPicker.querySelector(
          `input[type="radio"][name="${CSS.escape(nameAttr)}"][value="${CSS.escape(optionValue)}"]`
        );
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }

        const select = mainPicker.querySelector(`select[name="${CSS.escape(nameAttr)}"]`);
        if (select) {
          if (select.value !== optionValue) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }

        return false;
      }

      updateSellingPlanState(purchaseType) {
        if (!this.sellingPlanInput || !this.frequencySelect) {
          this.updatePriceDisplay();
          return;
        }
        const subscribing = purchaseType === 'subscription';

        this.frequencyWrapper?.toggleAttribute('hidden', !subscribing);
        this.frequencySelect.disabled = !subscribing;
        this.sellingPlanInput.disabled = !subscribing;
        this.sellingPlanInput.value = subscribing ? this.frequencySelect.value : '';

        this.updatePriceDisplay();
      }

      updatePriceDisplay() {
        if (!this.activePriceEl) return;

        const variantId = this.variantInput?.value ? String(this.variantInput.value) : null;
        const variantData = variantId
          ? this.priceData.variants.find((v) => String(v.id) === variantId)
          : null;

        if (!variantData) {
          if (this.comparePriceEl) {
            this.comparePriceEl.innerHTML = '';
            this.comparePriceEl.hidden = true;
          }
          return;
        }

        const subscribing = this.querySelector('[data-sticky-purchase-type]:checked')?.value === 'subscription';
        const planId = subscribing && this.frequencySelect && this.frequencySelect.value
          ? String(this.frequencySelect.value)
          : null;
        const allocation = planId
          ? variantData.allocations.find((a) => String(a.planId) === planId)
          : null;

        const basePriceHtml = variantData.priceHtml;
        const activePriceHtml = allocation ? allocation.priceHtml : basePriceHtml;
        const showCompare = !!allocation && allocation.priceHtml !== basePriceHtml;

        this.activePriceEl.innerHTML = activePriceHtml;

        if (this.comparePriceEl) {
          if (showCompare) {
            this.comparePriceEl.innerHTML = basePriceHtml;
            this.comparePriceEl.hidden = false;
          } else {
            this.comparePriceEl.innerHTML = '';
            this.comparePriceEl.hidden = true;
          }
        }

        if (this.priceContainer) {
          this.priceContainer.innerHTML = showCompare
            ? `<s class="price-item price-item--regular">${basePriceHtml}</s>` +
              `<span class="price-item price-item--sale">${activePriceHtml}</span>`
            : `<span class="price-item price-item--regular">${activePriceHtml}</span>`;
        }
      }
    }
  );
}
